import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Crown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useSubscription } from "@/lib/subscription";
import { useI18n } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import ReferralCard from "./ReferralCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionBadge() {
  const { t } = useI18n();
  const { trial_active, trial_days_left, subscription, refresh } = useSubscription();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const isPro = !!subscription && ["active", "trialing"].includes(subscription.status);

  async function openPortal() {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/billing/stripe/portal`, { return_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error(t("portal_only_stripe") || "Customer portal available for Stripe subscriptions only.");
    } finally { setBusy(false); }
  }

  async function cancel() {
    if (!window.confirm(t("confirm_cancel") || "Cancel your subscription at the end of the current period?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/billing/cancel`);
      toast.success(t("cancel_scheduled") || "Cancellation scheduled");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    } finally { setBusy(false); }
  }

  const label = isPro
    ? (t("pro") || "Pro")
    : trial_active
      ? `${t("trial") || "Trial"} · ${trial_days_left}${t("d") || "d"}`
      : (t("upgrade") || "Upgrade");

  const Icon = isPro ? Crown : Sparkles;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="subscription-badge"
          className={
            "inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-semibold transition " +
            (isPro
              ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-black hover:opacity-90"
              : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20")
          }
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg">{t("your_subscription") || "Your subscription"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {isPro ? (
            <>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                <div className="flex items-center gap-2 text-emerald-300 font-semibold">
                  <Crown className="w-4 h-4" /> {t("active_plan") || "Active plan"}
                </div>
                <div className="mt-1 text-zinc-300">
                  {subscription.lookup_key === "signals_yearly" ? (t("plan_yearly") || "Yearly") : (t("plan_monthly") || "Monthly")}
                  {" · "}{subscription.provider}
                </div>
                {subscription.current_period_end && (
                  <div className="text-xs text-zinc-500 mt-1">
                    {subscription.cancel_at_period_end ? (t("ends_on") || "Ends on") : (t("renews_on") || "Renews on")}
                    {" "}{new Date(subscription.current_period_end).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {subscription.provider === "stripe" && (
                  <Button data-testid="open-portal-btn" onClick={openPortal} disabled={busy} className="rounded-full bg-white text-black hover:bg-zinc-200">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (t("manage_billing") || "Manage billing")}
                  </Button>
                )}
                {!subscription.cancel_at_period_end && (
                  <Button data-testid="cancel-sub-btn" onClick={cancel} disabled={busy} variant="outline" className="rounded-full border-zinc-700">
                    {t("cancel_sub") || "Cancel subscription"}
                  </Button>
                )}
              </div>
            </>
          ) : trial_active ? (
            <>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                <div className="text-emerald-300 font-semibold">{t("trial_active") || "Free trial active"}</div>
                <div className="mt-1 text-zinc-300">
                  {t("days_remaining") || "Days remaining"}: <span className="font-bold text-white">{trial_days_left}</span>
                </div>
              </div>
              <Button data-testid="upgrade-btn" onClick={() => navigate("/pricing")} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                {t("upgrade_now") || "Upgrade now"}
              </Button>
            </>
          ) : (
            <Button onClick={() => navigate("/pricing")} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              {t("view_plans") || "View plans"}
            </Button>
          )}
          <ReferralCard />
        </div>
      </DialogContent>
    </Dialog>
  );
}
